import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import classnames from 'classnames';
import { Button } from 'reactstrap';

import { useIntl } from 'react-intl';

import { setUpgradeModal } from '../actions/App';

import { slugify } from '../helpers';
import { userHasFeature } from '../helpers/Features';
import { getUpgradePlanText } from '../helpers/UpgradePlan';

const UpgradePlanButton = ({
  handleUpgradeModal,
  features,
  id,
  className,
  feature,
  isInTrial,
  simple,
  flying,
  forceButton, // Set to true to force displaying the button
  featureObject, // Provide when feature doesn't come from global state
  responsive, // Set to true if should act like simple on small devices
  forceUpgrade,
  inactive,
}) => {
  const intl = useIntl();

  if ((!feature || (userHasFeature(feature) && !isInTrial)) && !forceButton) {
    return null;
  }

  const lockedFeature = featureObject || features.find(f => (f.slug === feature));
  if (!lockedFeature) {
    return null;
  }
  if ((!lockedFeature?.plans || lockedFeature?.plans.length === 0) && !forceButton) {
    return null;
  }

  const plan = lockedFeature.plans
    .reduce((lowest, featurePlan) => (featurePlan.position < lowest.position ?
      featurePlan : lowest), lockedFeature.plans[0]);

  let uId = id;
  if (!uId) {
    uId = plan.slug;
  }
  const elId = `upgrade-plan-button-${uId}-${slugify(feature)}`;

  const modal = getUpgradePlanText(feature, intl)?.modal;

  const handleButton = (e) => {
    if (!inactive) {
      e.preventDefault();
      e.stopPropagation();
    }
    if (modal) {
      handleUpgradeModal(feature);
    }
  };

  if ((features === undefined || isInTrial === undefined) && !forceButton) {
    return null;
  }

  return (
    <div className={classnames(
      'upgrade-plan-button',
      className,
      {
        'upgrade-plan-button-flying': flying,
        'upgrade-plan-button-active': !inactive,
        'upgrade-plan-button-not-reactive': !modal,
      },
    )}
    >
      <Button
        id={elId}
        data-tooltip-id={elId}
        className={classnames(
          'upgrade-plan-button-a',
          `upgrade-plan-button-${plan.slug}`,
          {
            'upgrade-plan-button-simple': simple,
            'upgrade-plan-button-responsive': responsive,
            'upgrade-plan-button-in-trial': isInTrial && !forceUpgrade,
          },
        )}
        color="primary"
        size="sm"
        onClick={handleButton}
        aria-label={intl.formatMessage({ defaultMessage: 'Upgrade to {planName} plan', values: { planName: plan.name } })}
      >
        <i className={classnames('fas fa-arrow-up', { 'mr-2': !simple })} />
        {!simple && <span className="upgrade-plan-button-plan-name">{plan.name}</span>}
      </Button>
    </div>
  );
};

const mapStateToProps = state => ({
  features: state.App.features,
  profileFeatures: state.Auth.profile?.features,
  client: state.Auth.profile?.client,
  isInTrial: state.Auth.profile?.client?.is_in_trial,
});

const mapDispatchToProps = dispatch => ({
  handleUpgradeModal: (modal) => {
    dispatch(setUpgradeModal(modal));
  },
});

UpgradePlanButton.propTypes = {
  handleUpgradeModal: PropTypes.func.isRequired,
  features: PropTypes.array,
  id: PropTypes.string,
  className: PropTypes.string,
  feature: PropTypes.string,
  isInTrial: PropTypes.bool,
  simple: PropTypes.bool,
  flying: PropTypes.bool,
  forceButton: PropTypes.bool,
  featureObject: PropTypes.object,
  responsive: PropTypes.bool,
  forceUpgrade: PropTypes.bool,
  inactive: PropTypes.bool,
};

UpgradePlanButton.defaultProps = {
  id: '',
  className: '',
  feature: null,
  simple: false,
  flying: false,
  features: [],
  isInTrial: false,
  forceButton: false,
  featureObject: null,
  responsive: false,
  forceUpgrade: false,
  inactive: false,
};

export default connect(mapStateToProps, mapDispatchToProps)(UpgradePlanButton);
